/**
 * NonEmptyString — a branded string type that cannot be the empty literal `""`.
 *
 * Used by primitives where an empty string would be a real accessibility defect:
 *   - `<Button size="icon">` `aria-label` (WCAG 4.1.2 — icon-only controls must
 *     have a non-empty accessible name).
 *   - `<Skeleton>` `aria-label` (`Loading {entity}` — empty entity = empty label).
 *   - `<Dialog>` / `<ConfirmDialog>` `title`.
 *   - `<EmptyState>` heading / CTA.
 *
 * The brand is a unique symbol so:
 *   1. `""` (empty literal) cannot be assigned — empty string has no brand.
 *   2. Generic `string` cannot be assigned — it might be empty at runtime.
 *   3. You MUST go through `nonEmpty(value)` or `isNonEmpty(value)`, which
 *      narrows at the boundary and brands the value for the type system.
 *
 * The runtime value is just a string; the brand exists only at compile time.
 *
 * @example
 * declare function greet(name: NonEmptyString): void;
 * greet("");                      // ✗ compile error — `""` has no brand
 * greet("Ada" as string);         // ✗ compile error — generic `string` not branded
 * greet(nonEmpty("Ada"));         // ✓ helper brands + throws if empty
 * if (isNonEmpty(maybeName)) {
 *   greet(maybeName);             // ✓ narrowed by the guard
 * }
 */

// Intentional unique-symbol brand keyed to this module.
const NonEmptyStringBrand: unique symbol = Symbol.for("@linkhr/design-system/NonEmptyString");

export type NonEmptyString = string & { readonly [NonEmptyStringBrand]: true };

/**
 * Runtime + compile-time boundary helper.
 *
 * Throws when `value` is empty (or would be empty after trimming). Returns the
 * same string branded as `NonEmptyString` so it can satisfy primitive props.
 * Use at the *producer* boundary (i18n translation output, component default
 * copy tables, consumer call-sites that already have a literal).
 */
export function nonEmpty(value: string): NonEmptyString {
  if (value.length === 0 || value.trim().length === 0) {
    throw new Error("nonEmpty(): value must be a non-empty string (was empty or whitespace-only).");
  }
  return value as NonEmptyString;
}

/**
 * Type guard — narrows `string` to `NonEmptyString` without throwing.
 *
 * Use when the string comes from an untrusted/nullable source (form state,
 * API response, query param) and the caller wants to branch instead of throw.
 */
export function isNonEmpty(value: string): value is NonEmptyString {
  return value.length > 0 && value.trim().length > 0;
}
